import {bbox} from '@turf/turf';
import geojson from 'geojson';
import isEqual from 'lodash/isEqual';
import {useEffect, useRef} from 'react';

import {BBox2d} from 'app/utils/geoJsonUtils';
import * as mapUtils from 'app/utils/mapUtils';

interface Props {
  map: mapboxgl.Map;
  geoJson: geojson.GeoJSON | null;
  fitBoundsOptions?: mapboxgl.EasingOptions;
  resizeBeforeFitting?: boolean;
  geoJsonComparison?: 'shallow' | 'deep';
}

export default function GeoJsonFitter({
  map,
  geoJson,
  fitBoundsOptions,
  resizeBeforeFitting,
  geoJsonComparison = 'deep',
}: Props) {
  const prevGeoJsonRef = useRef<Props['geoJson']>();
  const prevMapRef = useRef<Props['map']>();

  useEffect(() => {
    const prevGeoJson = prevGeoJsonRef.current;
    const prevMap = prevMapRef.current;

    const geoJsonChanged =
      geoJsonComparison === 'shallow' ? geoJson !== prevGeoJson : !isEqual(geoJson, prevGeoJson);

    const mapChanged = map !== prevMap;

    if ((geoJsonChanged || mapChanged) && geoJson) {
      if (resizeBeforeFitting) {
        map.resize();
      }

      const bounds = bbox(geoJson) as BBox2d;

      mapUtils.safeFitBounds(
        map,
        bounds,
        {
          ...fitBoundsOptions,
        },
        {source: 'GeoJsonFitter', bounds, padding: fitBoundsOptions?.padding}
      );
    }

    prevGeoJsonRef.current = geoJson;
    prevMapRef.current = map;
  }, [map, geoJson, geoJsonComparison, resizeBeforeFitting, fitBoundsOptions]);

  return null;
}
